import type { AccountDto } from '../../shared/types/index.js';

/** Persisted shield session; the account is a cache and is revalidated on load. */
export interface StoredSession {
  token: string;
  /** ISO timestamp. */
  expiresAt: string;
  account: AccountDto;
  /** Set while an admin acts as `account`. */
  impersonatedBy?: AccountDto;
  /** The admin's own session, restored when the impersonation ends. */
  original?: StoredSession;
}

const SESSION_KEY = 'shield.session';
const COUNTRY_KEY = 'shield.country';
const EDIT_MODE_KEY = 'shield.editMode';

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage may be unavailable (private mode); the session then lives only in memory.
  }
}

export function isExpired(session: Pick<StoredSession, 'expiresAt'>, now = Date.now()): boolean {
  const expiresAt = Date.parse(session.expiresAt);
  return !Number.isFinite(expiresAt) || expiresAt <= now;
}

function isSession(value: unknown): value is StoredSession {
  const s = value as Partial<StoredSession> | null;
  return !!s && typeof s.token === 'string' && typeof s.expiresAt === 'string' && !!s.account;
}

/**
 * Parses a stored session. An expired impersonation falls back to the admin's own session when that
 * one is still valid; an expired plain session yields null.
 */
export function parseSession(raw: string | null, now = Date.now()): StoredSession | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isSession(parsed)) return null;
    const original = isSession(parsed.original) && !isExpired(parsed.original, now) ? parsed.original : undefined;
    if (isExpired(parsed, now)) return original ?? null;
    return { ...parsed, original };
  } catch {
    return null;
  }
}

export function readSession(now = Date.now()): StoredSession | null {
  return parseSession(safeGet(SESSION_KEY), now);
}

export function writeSession(session: StoredSession | null): void {
  safeSet(SESSION_KEY, session ? JSON.stringify(session) : null);
}

/** The selected data-scope country, kept only if the account may see it. */
export function readCountry(allowed: ReadonlyArray<string>): string | undefined {
  const stored = safeGet(COUNTRY_KEY)?.toUpperCase();
  return stored && allowed.includes(stored) ? stored : allowed[0];
}

export function writeCountry(country: string | null): void {
  safeSet(COUNTRY_KEY, country);
}

/** Edit mode (admin rights while impersonating) is on unless explicitly switched off. */
export function readEditMode(): boolean {
  return safeGet(EDIT_MODE_KEY) !== '0';
}

export function writeEditMode(on: boolean): void {
  safeSet(EDIT_MODE_KEY, on ? null : '0');
}
